import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { useAuth, SignedIn, SignedOut } from '@/contexts/AuthContext'
import { useTheme, type Theme } from '@/contexts/ThemeContext'
import { Camera, Settings, LogOut, Sun, Moon, MoonStar } from 'lucide-react'

// Root-relative so the anchors also work from /pricing and /aesthetics
// (hash-only when already on '/', so no reload there).
const NAV_LINKS = [
  { label: 'How it works', href: '/#how' },
  { label: 'Features', href: '/#features' },
  { label: 'Use cases', href: '/#usecases' },
]

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2 group" style={{ color: 'hsl(var(--foreground))' }}>
      <span
        className="grid place-items-center text-white"
        style={{
          width: 30,
          height: 30,
          borderRadius: 9,
          background: 'linear-gradient(135deg, hsl(var(--gradient-start)), hsl(var(--gradient-middle)), hsl(var(--gradient-end)))',
        }}
      >
        <Camera style={{ width: 17, height: 17 }} />
      </span>
      <span className="fdisplay" style={{ fontWeight: 800, fontSize: '1.32rem', letterSpacing: '-.02em' }}>
        photodrop
      </span>
    </Link>
  )
}

const THEME_OPTS: [Theme, typeof Sun][] = [
  ['light', Sun],
  ['dark', Moon],
  ['ultra-dark', MoonStar],
]

function ThemeSeg() {
  const { resolvedTheme, setTheme } = useTheme()
  return (
    <div className="pd-seg" role="group" aria-label="Theme">
      {THEME_OPTS.map(([id, Icon]) => (
        <button
          key={id}
          className={resolvedTheme === id ? 'active' : ''}
          onClick={() => setTheme(id)}
          aria-pressed={resolvedTheme === id}
          title={id}
        >
          <Icon style={{ width: 14, height: 14 }} />
        </button>
      ))}
    </div>
  )
}

export function Nav() {
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24)
    on()
    window.addEventListener('scroll', on, { passive: true })
    return () => window.removeEventListener('scroll', on)
  }, [])

  return (
    <nav className={'pd-nav ' + (scrolled ? 'scrolled' : '')}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '16px 28px', display: 'flex', alignItems: 'center', gap: 22 }}>
        <Logo />
        <div className="pd-navlinks" style={{ marginLeft: 18, display: 'flex', alignItems: 'center', gap: 28 }}>
          {NAV_LINKS.map((l) => (
            <a key={l.label} href={l.href} className="pd-navlink">{l.label}</a>
          ))}
          <Link to="/pricing" className="pd-navlink">Pricing</Link>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="pd-hide-sm" style={{ display: 'inline-flex' }}><ThemeSeg /></span>
          <SignedOut>
            <Link to="/login" className="pd-navlink pd-hide-sm" style={{ marginRight: 2 }}>Sign in</Link>
            <span className="pd-gbtn">
              <Button asChild variant="playful"><Link to="/signup">Create a drop</Link></Button>
            </span>
          </SignedOut>
          <SignedIn>
            <Link to="/dashboard" className="pd-navlink pd-hide-sm" style={{ marginRight: 2 }}>Dashboard</Link>
            <UserMenu />
          </SignedIn>
        </div>
      </div>
    </nav>
  )
}

function UserMenu() {
  const { user, signOut } = useAuth()
  const initial = user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'
  return (
    <div className="relative group">
      <button className="flex items-center rounded-full hover:scale-105 transition-transform">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-secondary text-primary-foreground flex items-center justify-center text-sm font-semibold shadow-md">
          {initial}
        </div>
      </button>
      <div className="absolute right-0 mt-2 w-52 rounded-xl shadow-lg bg-card border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 overflow-hidden">
        <div className="py-1">
          <div className="px-4 py-3 border-b bg-muted/30">
            <p className="font-semibold text-sm">{user?.name || 'User'}</p>
            <p className="text-muted-foreground text-xs truncate">{user?.email}</p>
          </div>
          <Link to="/settings" className="flex items-center px-4 py-2.5 text-sm hover:bg-muted transition-colors">
            <Settings className="mr-3 h-4 w-4" />
            Settings
          </Link>
          <button
            onClick={() => signOut()}
            className="flex items-center w-full text-left px-4 py-2.5 text-sm hover:bg-muted text-destructive transition-colors"
          >
            <LogOut className="mr-3 h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>
    </div>
  )
}
